import React, { useEffect, useState } from "react";
import {
  IonApp,
  IonPage,
  IonHeader,
  IonToolbar,
  IonTitle,
  IonContent,
  IonButtons,
  IonMenuButton,
  IonToast,
  IonLoading,
} from "@ionic/react";
import {
  Box,
  TextField,
  MenuItem,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Paper,
  Select,
  InputLabel,
  FormControl,
  Button,
  Typography,
  TablePagination,
  InputAdornment,
  Card,
  CardContent,
  Grid,
  Chip,
  Avatar,
  LinearProgress,
} from "@mui/material";
import {
  Search,
  DateRange,
  TrendingUp,
  Person,
  Inventory,
  ArrowBack,
  Summarize,
} from "@mui/icons-material";
import axios from "axios";
import { baseUrl, config, requestID } from "../../../common/utils";

const SalesAnalysis: React.FC = () => {
  const [filterType, setFilterType] = useState("stock");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [filteredSales, setFilteredSales] = useState<any[]>([]);
  const [selectedItem, setSelectedItem] = useState("");
  const [breakdown, setBreakdown] = useState<any[]>([]);
  const [dateError, setDateError] = useState(false);
  const [status, setStatus] = useState({ loading: false, error: false });
  const [salesAnalysis, setSalesAnalysis] = useState<any[]>([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage] = useState(100);
  const [toastMessage, setToastMessage] = useState("");
  const [showToast, setShowToast] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");

  async function getSalesAnalysis() {
    setStatus({ loading: true, error: false });

    try {
      const formData = new FormData();
      formData.append("requestID", requestID());
      formData.append("start_date", startDate);
      formData.append("end_date", endDate);
      formData.append("reporttype", filterType);

      const response = await axios.post(
        baseUrl() + "Orderhistorys/stockCustomerOrdersAnalysis",
        formData,
        config
      );
      console.log("Sales analysis data:", response);

      const formattedData = response.data.map((item: any) => ({
        name:
          filterType === "stock"
            ? item.product_name
            : `${item.firstName || ""} ${item.lastName || ""}`.trim(),
        total: Number(item.total_amount),
        full: item,
      }));

      setFilteredSales(formattedData);
      setSalesAnalysis(response.data);
      setPage(0);
      setStatus({ loading: false, error: false });
    } catch (error) {
      console.error("Error:", error);
      setStatus({ loading: false, error: true });
    }
  }

  const handleShowResults = () => {
    if (!startDate || !endDate) {
      setDateError(true);
      return;
    }
    setDateError(false);
    setSelectedItem("");
    setBreakdown([]);
    getSalesAnalysis();
  };

  const handleViewBreakdown = async (productId: string, productName: string) => {
    setStatus({ loading: true, error: false });

    try {
      const formData = new FormData();
      formData.append("requestID", requestID());
      formData.append("start_date", startDate);
      formData.append("end_date", endDate);
      formData.append("product_id", productId);

      const response = await axios.post(
        baseUrl() + "Orderhistorys/stockCustomerOrdersAnalysis_stock",
        formData,
        config
      );

      const productBreakdown =
        response.data && Array.isArray(response.data.data)
          ? response.data.data
          : [];

      if (productBreakdown.length === 0) {
        setToastMessage("No order details found for this product.");
        setShowToast(true);
        setStatus({ loading: false, error: false });
        return;
      }

      setSelectedItem(productName);
      setBreakdown(productBreakdown);
      setStatus({ loading: false, error: false });
    } catch (error) {
      console.error("Error fetching stock breakdown:", error);
      setStatus({ loading: false, error: true });
      setToastMessage("Failed to fetch product order details.");
      setShowToast(true);
    }
  };

  const handleViewBreakdownCust = async (
    memberId: string,
    customerName: string
  ) => {
    setStatus({ loading: true, error: false });

    try {
      const formData = new FormData();
      formData.append("requestID", requestID());
      formData.append("start_date", startDate);
      formData.append("end_date", endDate);
      formData.append("member_id", memberId);

      const response = await axios.post(
        baseUrl() + "Orderhistorys/stockCustomerOrdersAnalysis_individual",
        formData,
        config
      );

      const orders = response.data && response.data.data ? response.data.data : [];
      const productBreakdown = Array.isArray(orders)
        ? orders.flatMap((order: any) => {
            const orderItems = Array.isArray(order.items) ? order.items : [];
            return orderItems.map((item: any) => ({
              ...item,
              order_date: order.order_date,
              customer_name: customerName,
            }));
          })
        : [];

      if (productBreakdown.length === 0) {
        setToastMessage("No order details found for this customer.");
        setShowToast(true);
        setStatus({ loading: false, error: false });
        return;
      }

      setSelectedItem(customerName);
      setBreakdown(productBreakdown);
      setStatus({ loading: false, error: false });
    } catch (error) {
      console.error("Error fetching customer breakdown:", error);
      setStatus({ loading: false, error: true });
      setToastMessage("Failed to fetch customer order details.");
      setShowToast(true);
    }
  };

  const handleBackToSummary = () => {
    setSelectedItem("");
    setBreakdown([]);
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const filteredData = filteredSales.filter((item) =>
    item.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const totalAmount = breakdown.reduce(
    (acc, item) => acc + Number(item.total_amount || 0),
    0
  );

  const totalSales = filteredSales.reduce(
    (acc, item) => acc + Number(item.total || 0),
    0
  );

  return (
    <IonPage className="pushDown">
      <IonHeader>
        <IonToolbar
          style={{
            background: "white",
            boxShadow: "0 2px 12px rgba(0,0,0,0.1)",
          }}
        >
          <IonButtons slot="start">
            <IonMenuButton style={{ color: "#1976d2" }} />
          </IonButtons>
          <IonTitle style={{ color: "#333", fontWeight: 600 }}>
            Sales Analysis
          </IonTitle>
        </IonToolbar>
      </IonHeader>

      <IonContent
        className="ion-padding"
        style={{ backgroundColor: "#f8fafc" }}
      >
        <IonLoading
          isOpen={status.loading}
          message={"Loading..."}
          spinner="circles"
          cssClass="custom-loading"
        />

        {/* Header Section */}
        <Box mb={3}>
          <Typography variant="h5" fontWeight="600" color="#333" gutterBottom>
            Sales Analysis Dashboard
          </Typography>
          <Typography variant="body2" color="#666">
            Analyze sales performance by stock or customer
          </Typography>
        </Box>

        {/* Summary Cards */}
        {filteredSales.length > 0 && !selectedItem && (
          <Grid container spacing={2} mb={3}>
            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{ borderRadius: 2, boxShadow: 2 }}>
                <CardContent>
                  <Box display="flex" alignItems="center">
                    <Avatar sx={{ bgcolor: "#e3f2fd", mr: 2 }}>
                      <TrendingUp sx={{ color: "#1976d2" }} />
                    </Avatar>
                    <Box>
                      <Typography variant="h6" fontWeight="600">
                        ₦{totalSales.toLocaleString()}
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        Total Sales
                      </Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{ borderRadius: 2, boxShadow: 2 }}>
                <CardContent>
                  <Box display="flex" alignItems="center">
                    <Avatar sx={{ bgcolor: "#fff0e5", mr: 2 }}>
                      {filterType === "stock" ? (
                        <Inventory sx={{ color: "#ff6e40" }} />
                      ) : (
                        <Person sx={{ color: "#ff6e40" }} />
                      )}
                    </Avatar>
                    <Box>
                      <Typography variant="h6" fontWeight="600">
                        {filteredSales.length}
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        {filterType === "stock" ? "Products" : "Customers"}
                      </Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        )}

        {/* Filter Section */}
        <Card sx={{ mb: 3, borderRadius: 2, boxShadow: 3 }}>
          <CardContent>
            <Typography variant="h6" color="#333" fontWeight="500" gutterBottom>
              Filter Criteria
            </Typography>

            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={3}>
                <FormControl fullWidth>
                  <InputLabel>Filter By</InputLabel>
                  <Select
                    value={filterType}
                    label="Filter By"
                    onChange={(e) => setFilterType(e.target.value)}
                  >
                    <MenuItem value="stock">
                      <Box display="flex" alignItems="center">
                        <Inventory sx={{ fontSize: 18, mr: 1 }} />
                        Stock Name
                      </Box>
                    </MenuItem>
                    <MenuItem value="customer">
                      <Box display="flex" alignItems="center">
                        <Person sx={{ fontSize: 18, mr: 1 }} />
                        Customer Name
                      </Box>
                    </MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} md={3}>
                <TextField
                  label="Start Date"
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                  error={dateError && !startDate}
                  helperText={
                    dateError && !startDate ? "Start date is required" : ""
                  }
                />
              </Grid>

              <Grid item xs={12} md={3}>
                <TextField
                  label="End Date"
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                  error={dateError && !endDate}
                  helperText={
                    dateError && !endDate ? "End date is required" : ""
                  }
                />
              </Grid>

              <Grid item xs={12} md={3}>
                <Button
                  variant="contained"
                  fullWidth
                  onClick={handleShowResults}
                  disabled={status.loading}
                  startIcon={<DateRange />}
                  sx={{ py: 1.5, fontWeight: 500 }}
                >
                  {status.loading ? "Loading..." : "Apply Filters"}
                </Button>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* Search and Results Section */}
        {filteredSales.length > 0 && !selectedItem && (
          <Card sx={{ borderRadius: 2, boxShadow: 3 }}>
            <CardContent>
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                mb={2}
              >
                <Typography variant="h6" color="#333" fontWeight="500">
                  {filterType === "stock" ? "Product Sales" : "Customer Sales"}
                </Typography>

                <TextField
                  placeholder="Search..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Search />
                      </InputAdornment>
                    ),
                  }}
                  sx={{ width: 300 }}
                  size="small"
                />
              </Box>

              <Table>
                <TableHead>
                  <TableRow sx={{ backgroundColor: "#f5f7fa" }}>
                    {filterType === "stock" ? (
                      <>
                        <TableCell sx={{ fontWeight: 600 }}>Product</TableCell>
                        <TableCell sx={{ fontWeight: 600 }} align="right">
                          Total Amount
                        </TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Actions</TableCell>
                      </>
                    ) : (
                      <>
                        <TableCell sx={{ fontWeight: 600 }}>Customer</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Phone</TableCell>
                        <TableCell sx={{ fontWeight: 600 }} align="right">
                          Total Amount
                        </TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Actions</TableCell>
                      </>
                    )}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredData
                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                    .map((row, index) => {
                      const { full } = row;
                      return (
                        <TableRow
                          key={index}
                          sx={{
                            "&:hover": { backgroundColor: "#f5f7fa" },
                            borderBottom:
                              index < filteredData.length - 1
                                ? "1px solid #eaeaea"
                                : "none",
                          }}
                        >
                          {filterType === "stock" ? (
                            <>
                              <TableCell>
                                <Box display="flex" alignItems="center">
                                  <Avatar
                                    sx={{
                                      bgcolor: "#e3f2fd",
                                      width: 32,
                                      height: 32,
                                      mr: 2,
                                    }}
                                  >
                                    <Inventory
                                      sx={{ fontSize: 18, color: "#1976d2" }}
                                    />
                                  </Avatar>
                                  <Typography variant="body2" fontWeight="500">
                                    {row.name}
                                  </Typography>
                                </Box>
                              </TableCell>
                              <TableCell align="right">
                                <Typography variant="body2" fontWeight="500">
                                  ₦{row.total.toLocaleString()}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                <Button
                                  onClick={() =>
                                    handleViewBreakdown(
                                      full.product_id,
                                      row.name
                                    )
                                  }
                                  variant="outlined"
                                  size="small"
                                  startIcon={<Summarize />}
                                >
                                  Details
                                </Button>
                              </TableCell>
                            </>
                          ) : (
                            <>
                              <TableCell>
                                <Box display="flex" alignItems="center">
                                  <Avatar
                                    sx={{
                                      bgcolor: "#fff0e5",
                                      width: 32,
                                      height: 32,
                                      mr: 2,
                                    }}
                                  >
                                    <Person
                                      sx={{ fontSize: 18, color: "#ff6e40" }}
                                    />
                                  </Avatar>
                                  <Typography variant="body2" fontWeight="500">
                                    {row.name}
                                  </Typography>
                                </Box>
                              </TableCell>
                              <TableCell>
                                <Typography variant="body2">
                                  {full.phone || "-"}
                                </Typography>
                              </TableCell>
                              <TableCell align="right">
                                <Typography variant="body2" fontWeight="500">
                                  ₦{row.total.toLocaleString()}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                <Button
                                  onClick={() =>
                                    handleViewBreakdownCust(
                                      full.user_id,
                                      row.name
                                    )
                                  }
                                  variant="outlined"
                                  size="small"
                                  startIcon={<Summarize />}
                                >
                                  Details
                                </Button>
                              </TableCell>
                            </>
                          )}
                        </TableRow>
                      );
                    })}
                </TableBody>
              </Table>

              {filteredData.length === 0 && (
                <Box textAlign="center" py={4}>
                  <Typography variant="body2" color="textSecondary">
                    No results found for "{searchQuery}"
                  </Typography>
                </Box>
              )}

              <TablePagination
                component="div"
                count={filteredData.length}
                page={page}
                onPageChange={handleChangePage}
                rowsPerPage={rowsPerPage}
                rowsPerPageOptions={[rowsPerPage]}
              />
            </CardContent>
          </Card>
        )}

        {/* Breakdown View */}
        {selectedItem && breakdown.length > 0 && (
          <Card sx={{ borderRadius: 2, boxShadow: 3 }}>
            <CardContent>
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                mb={2}
              >
                <Box display="flex" alignItems="center">
                  <Button
                    onClick={handleBackToSummary}
                    startIcon={<ArrowBack />}
                    sx={{ mr: 2 }}
                  >
                    Back
                  </Button>
                  <Typography variant="h6" color="#333" fontWeight="500">
                    {filterType === "stock"
                      ? `Order Items for: ${selectedItem}`
                      : `Orders for: ${selectedItem}`}
                  </Typography>
                </Box>

                <Chip
                  label={`Total: ₦${totalAmount.toLocaleString()}`}
                  color="primary"
                  variant="outlined"
                />
              </Box>

              <Table>
                <TableHead>
                  <TableRow sx={{ backgroundColor: "#f5f7fa" }}>
                    <TableCell sx={{ fontWeight: 600 }}>
                      Date of Transaction
                    </TableCell>

                    <TableCell sx={{ fontWeight: 600 }}>
                      Customer Name
                    </TableCell>
 <TableCell sx={{ fontWeight: 600 }}>
                      Staff Name
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Product Name</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Sale Price </TableCell>

                    <TableCell sx={{ fontWeight: 600 }} align="right">
                      Quantity
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600 }} align="right">
                      Total Amount (₦)
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {breakdown.map((item, index) => (
                    <TableRow
                      key={index}
                      sx={{
                        "&:hover": { backgroundColor: "#f5f7fa" },
                        borderBottom:
                          index < breakdown.length - 1
                            ? "1px solid #eaeaea"
                            : "none",
                      }}
                    >
                      <TableCell>{item.order_date}</TableCell>
                      <TableCell>{item.customer_name}</TableCell>
                      <TableCell>{item.staff_name}</TableCell>
                        <TableCell>{item.product_name}</TableCell>
                          <TableCell>{item.price}</TableCell>
                      <TableCell align="right">{item.quantity}</TableCell>
                      <TableCell align="right">
                        ₦{Number(item.total_amount).toLocaleString()}
                      </TableCell>
                    </TableRow>
                  ))}
                  <TableRow sx={{ backgroundColor: "#e3f2fd" }}>
                    <TableCell colSpan={5} />
                    <TableCell align="right" sx={{ fontWeight: 600 }}>
                      Total
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600 }}>
                      ₦{totalAmount.toLocaleString()}
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        )}

        <IonToast
          isOpen={showToast}
          onDidDismiss={() => setShowToast(false)}
          message={toastMessage}
          duration={2000}
          position="top"
          color="warning"
        />
      </IonContent>
    </IonPage>
  );
};

export default SalesAnalysis;
